import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, 
  Target, 
  Calendar,
  ChevronRight,
  Check,
  Shield,
  Zap,
  BarChart3
} from 'lucide-react';
import { FigIcon } from '../components/FigIcon';

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const features = [
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Metabolic Risk Score",
      description: "Evidence-based scoring algorithm identifies your visceral fat and insulin resistance trajectory"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Personalized 90-Day Plan",
      description: "4 measurable rules tailored to your top risk drivers. Simple, binary, effective."
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Weekly Check-ins",
      description: "Track waist, energy, sleep, and adherence. Takes under 2 minutes."
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Progress Dashboard",
      description: "Visual feedback on your trajectory. See the data that matters."
    }
  ];

  const faqs = [
    {
      question: "What does Fig Health measure?",
      answer: "Fig Health calculates your metabolic risk based on waist circumference, sleep patterns, alcohol consumption, activity levels, nutrition timing, stress, and family history. It's focused on visceral fat and insulin resistance—the key drivers of metabolic disease. See our Methodology page for the full scientific basis."
    },
    {
      question: "Is this medical advice?",
      answer: "No. Fig Health provides education about metabolic risk and course-correction strategies. It is not a diagnosis, treatment, or medical advice. Always consult a healthcare provider for medical concerns."
    },
    {
      question: "How is my score calculated?",
      answer: "Your score is calculated using a weighted algorithm based on peer-reviewed research. Each risk factor (waist, sleep, alcohol, activity, nutrition, stress, family history) is weighted according to its contribution to metabolic dysfunction. Visit our Methodology page for detailed sources and citations."
    },
    {
      question: "How long does the questionnaire take?",
      answer: "The intake questionnaire takes 10-12 minutes. It's comprehensive because accurate scoring requires quality data."
    },
    {
      question: "What's in the 90-day plan?",
      answer: "Your plan includes 4 specific, measurable rules targeting your top risk drivers. Examples: 'No calories after 8pm 5 nights/week' or '10-minute walk after dinner'. Simple rules, measurable outcomes."
    },
    {
      question: "Can I cancel my subscription?",
      answer: "Yes. You can cancel anytime through your account settings. If you chose the subscription option, you'll retain access until the end of your billing period."
    }
  ];

  return (
    <div className="min-h-screen bg-[#E8E4DC]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <FigIcon className="w-8 h-8 text-[#2F3E30]" />
            <span className="font-bold text-xl tracking-widest text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>FIG HEALTH</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/why-metabolic-health" className="text-sm font-medium text-[#525252] hover:text-[#1A1C19] transition-colors uppercase tracking-wide">Why It Matters</Link>
            <a href="#features" className="text-sm font-medium text-[#525252] hover:text-[#1A1C19] transition-colors uppercase tracking-wide">Features</a>
            <a href="#pricing" className="text-sm font-medium text-[#525252] hover:text-[#1A1C19] transition-colors uppercase tracking-wide">Pricing</a>
            <a href="#faq" className="text-sm font-medium text-[#525252] hover:text-[#1A1C19] transition-colors uppercase tracking-wide">FAQ</a>
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <Button 
                onClick={() => navigate('/dashboard')} 
                className="bg-[#2F3E30] hover:bg-[#3A4D3B] text-white uppercase tracking-widest"
                data-testid="dashboard-btn"
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  className="text-[#525252] hover:text-white uppercase tracking-wide"
                  data-testid="login-btn"
                >
                  Log in
                </Button>
                <Button 
                  onClick={() => navigate('/register')} 
                  className="bg-[#2F3E30] hover:bg-[#3A4D3B] text-white uppercase tracking-widest"
                  data-testid="get-started-btn"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#D1D1CD] bg-white mb-6">
                <Zap className="w-4 h-4 text-[#854D0E]" />
                <span className="text-sm text-[#525252] uppercase tracking-wide">Free assessment • 3 minutes</span>
              </div>
              <h1 className="text-4xl lg:text-6xl font-bold tracking-wide mb-6 text-[#1A1C19] leading-tight" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                MOST MEN OVER 40 ARE ON THE WRONG<br />
                <span className="text-[#2F3E30]">METABOLIC TRAJECTORY</span>
              </h1>
              <p className="text-xl text-[#1A1C19] mb-8 max-w-lg leading-relaxed">
                Get your personal metabolic risk score, understand exactly what's driving it, and follow a 90-day plan built for your body.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/questionnaire')}
                  className="bg-[#2F3E30] hover:bg-[#3A4D3B] text-white text-lg px-8 uppercase tracking-widest"
                  data-testid="hero-cta-btn"
                >
                  Find Out Where You Stand
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-[#1A1C19] text-[#1A1C19] hover:bg-[#1A1C19] hover:text-white uppercase tracking-widest"
                  data-testid="view-pricing-btn"
                >
                  View Pricing
                </Button>
              </div>
            </div>
            <div className="relative animate-fade-in stagger-2">
              <div className="relative overflow-hidden border border-[#D1D1CD]">
                <img 
                  src="https://images.unsplash.com/photo-1760464907224-0a1dda1596b9?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Man trail running in nature"
                  className="w-full h-[420px] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#E8E4DC] via-transparent to-transparent opacity-60" />
              </div>
              <div className="mt-4">
                <div className="bg-white border border-[#D1D1CD] p-4">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-12 h-12 bg-[#2F3E30]/10 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-[#2F3E30]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#525252] uppercase tracking-wide">Your Score</p>
                      <p className="text-2xl font-bold text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>42 <span className="text-sm font-normal text-[#854D0E]">Moderate</span></p>
                    </div>
                  </div>
                  <div className="h-2 bg-[#EBEBE6] overflow-hidden">
                    <div className="h-full w-[42%] bg-gradient-to-r from-[#3F6212] via-[#854D0E] to-[#991B1B]" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-[#1A1C19] tracking-widest" style={{ fontFamily: 'Libre Baskerville, serif' }}>
              HOW IT WORKS
            </h2>
            <p className="text-[#525252] max-w-2xl mx-auto">
              A systematic approach to understanding and correcting metabolic risk
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-[#EBEBE6] border border-[#D1D1CD] p-6 hover:border-[#2F3E30] transition-colors animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 bg-[#2F3E30]/10 flex items-center justify-center text-[#2F3E30] mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-2 text-[#1A1C19] tracking-wide" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                  {feature.title.toUpperCase()}
                </h3>
                <p className="text-[#525252] text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 lg:px-12 bg-[#EBEBE6]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-[#1A1C19] tracking-widest" style={{ fontFamily: 'Libre Baskerville, serif' }}>
              SIMPLE PRICING
            </h2>
            <p className="text-[#525252] max-w-2xl mx-auto">
              Free assessment. Pay only when you're ready to see your results.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Results Only */}
            <div className="bg-white border border-[#D1D1CD] p-8 hover:border-[#2F3E30] transition-colors">
              <h3 className="text-xl font-bold mb-1 text-[#1A1C19] tracking-widest" style={{ fontFamily: 'Libre Baskerville, serif' }}>RESULTS ONLY</h3>
              <p className="text-[#525252] text-sm mb-4">See your score</p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>£5</span>
                <span className="text-[#525252] text-sm">one-time</span>
              </div>
              <ul className="space-y-3 mb-8 text-sm">
                {['Metabolic Score (0-100)', 'Risk tier assessment', 'Top 5 risk drivers', 'Score breakdown'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#3F6212]" />
                    <span className="text-[#1A1C19]">{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-transparent border border-[#1A1C19] text-[#1A1C19] hover:bg-[#1A1C19] hover:text-white uppercase tracking-widest"
                onClick={() => navigate(user ? '/checkout?package=results' : '/register?redirect=checkout&package=results')}
                data-testid="results-purchase-btn"
              >
                Get Results
              </Button>
            </div>
            {/* Full Program */}
            <div className="bg-white border-2 border-[#2F3E30] p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#2F3E30] text-white text-xs font-bold uppercase tracking-widest">
                Popular
              </div>
              <h3 className="text-xl font-bold mb-1 text-[#1A1C19] tracking-widest" style={{ fontFamily: 'Libre Baskerville, serif' }}>FULL PROGRAM</h3>
              <p className="text-[#525252] text-sm mb-4">Results + 90-day plan</p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>£49</span>
                <span className="text-[#525252] text-sm">one-time</span>
              </div>
              <ul className="space-y-3 mb-8 text-sm">
                {['Everything in Results', 'Personalized 90-day plan', 'Weekly check-ins', 'Progress dashboard', 'Email reminders'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#3F6212]" />
                    <span className="text-[#1A1C19]">{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-[#2F3E30] hover:bg-[#3A4D3B] text-white uppercase tracking-widest"
                onClick={() => navigate(user ? '/checkout?package=one_time' : '/register?redirect=checkout&package=one_time')}
                data-testid="one-time-purchase-btn"
              >
                Get Full Program
              </Button>
            </div>
            {/* Subscription */}
            <div className="bg-white border border-[#D1D1CD] p-8 hover:border-[#2F3E30] transition-colors relative">
              <div className="absolute top-4 right-4 px-2 py-1 bg-[#854D0E]/10 text-[#854D0E] text-xs font-bold uppercase tracking-wide">
                Flexible
              </div>
              <h3 className="text-xl font-bold mb-1 text-[#1A1C19] tracking-widest" style={{ fontFamily: 'Libre Baskerville, serif' }}>3-MONTH PLAN</h3>
              <p className="text-[#525252] text-sm mb-4">Monthly payments</p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>£19</span>
                <span className="text-[#525252] text-sm">/mo × 3</span>
              </div>
              <ul className="space-y-3 mb-8 text-sm">
                {['Everything in Full', 'Lower upfront cost', 'Cancel anytime', 'Priority support', 'Plan adjustments'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#3F6212]" />
                    <span className="text-[#1A1C19]">{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-transparent border border-[#1A1C19] text-[#1A1C19] hover:bg-[#1A1C19] hover:text-white uppercase tracking-widest"
                onClick={() => navigate(user ? '/checkout?package=subscription' : '/register?redirect=checkout&package=subscription')}
                data-testid="subscription-purchase-btn"
              >
                Start Monthly
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6 lg:px-12 bg-[#E8E4DC]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-[#1A1C19] tracking-widest" style={{ fontFamily: 'Libre Baskerville, serif' }}>
              FREQUENTLY ASKED
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details 
                key={index}
                className="group bg-[#EBEBE6] border border-[#D1D1CD] p-6 cursor-pointer hover:border-[#2F3E30] transition-colors"
              >
                <summary className="flex items-center justify-between font-medium text-lg list-none text-[#1A1C19]">
                  {faq.question}
                  <ChevronRight className="w-5 h-5 text-[#525252] transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-4 text-[#525252] text-sm leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-12 px-6 lg:px-12 bg-[#EBEBE6] border-t border-[#D1D1CD]">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-4 p-6 bg-white border border-[#D1D1CD]">
            <Shield className="w-6 h-6 text-[#525252] flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-bold mb-2 text-[#1A1C19] tracking-widest" style={{ fontFamily: 'Libre Baskerville, serif' }}>MEDICAL DISCLAIMER</h4>
              <p className="text-[#525252] text-sm leading-relaxed">
                Fig Health provides educational information about metabolic health risk factors. 
                This is not medical advice, a diagnosis, or treatment. The scoring algorithm is based on 
                published research but is not a substitute for professional medical evaluation. 
                If you have concerning symptoms or health conditions, consult a qualified healthcare provider. 
                Fig Health does not order labs, prescribe treatments, or provide medical diagnoses.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 border-t border-[#D1D1CD] bg-[#E8E4DC]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <FigIcon className="w-6 h-6 text-[#2F3E30]" />
                <span className="font-bold text-lg text-[#1A1C19] tracking-widest" style={{ fontFamily: 'Libre Baskerville, serif' }}>FIG HEALTH</span>
              </div>
              <p className="text-sm text-[#525252]">
                Know your metabolic trajectory. Course-correct before it's too late.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-[#1A1C19] tracking-widest" style={{ fontFamily: 'Libre Baskerville, serif' }}>PRODUCT</h4>
              <ul className="space-y-2 text-sm text-[#525252]">
                <li><a href="#features" className="hover:text-[#1A1C19] transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-[#1A1C19] transition-colors">Pricing</a></li>
                <li><a href="#faq" className="hover:text-[#1A1C19] transition-colors">FAQ</a></li>
                <li><Link to="/methodology" className="hover:text-[#1A1C19] transition-colors">Methodology</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-[#1A1C19] tracking-widest" style={{ fontFamily: 'Libre Baskerville, serif' }}>LEGAL</h4>
              <ul className="space-y-2 text-sm text-[#525252]">
                <li><Link to="/privacy" className="hover:text-[#1A1C19] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-[#1A1C19] transition-colors">Terms of Service</Link></li>
                <li><Link to="/disclaimer" className="hover:text-[#1A1C19] transition-colors">Medical Disclaimer</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-[#1A1C19] tracking-widest" style={{ fontFamily: 'Libre Baskerville, serif' }}>CONTACT</h4>
              <p className="text-sm text-[#525252]">
                support@fighealth.co
              </p>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-[#D1D1CD] text-center text-sm text-[#525252]">
            © {new Date().getFullYear()} Fig Health. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Noise overlay */}
      <div className="noise-overlay" />
    </div>
  );
};

export default LandingPage;
